/**
 * Training Compliance Reporting Page
 * Enterprise-grade compliance tracking with automated alerts
 */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Shield, Users, AlertTriangle, CheckCircle2, Clock, Bell,
  Settings, Download, RefreshCw, ChevronRight, Mail, Inbox,
  TrendingUp, TrendingDown, AlertCircle, FileText, Search,
  Check, X, Eye, Calendar, BarChart3, PieChart, Filter,
  User, GraduationCap, Award, Gauge
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL;

// Status configuration
const statusConfig = {
  compliant: { 
    color: 'bg-emerald-500', 
    bgLight: 'bg-emerald-50 dark:bg-emerald-900/20',
    text: 'text-emerald-700 dark:text-emerald-300',
    label: 'Compliant',
    icon: CheckCircle2
  },
  expiring_soon: { 
    color: 'bg-amber-500', 
    bgLight: 'bg-amber-50 dark:bg-amber-900/20',
    text: 'text-amber-700 dark:text-amber-300',
    label: 'Expiring Soon',
    icon: Clock
  },
  non_compliant: { 
    color: 'bg-orange-500', 
    bgLight: 'bg-orange-50 dark:bg-orange-900/20',
    text: 'text-orange-700 dark:text-orange-300',
    label: 'Non-Compliant',
    icon: AlertTriangle
  },
  overdue: { 
    color: 'bg-red-500', 
    bgLight: 'bg-red-50 dark:bg-red-900/20',
    text: 'text-red-700 dark:text-red-300',
    label: 'Overdue',
    icon: AlertCircle
  },
};

// Severity colors
const severityColors = {
  low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

export default function TrainingCompliancePage() {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  // Data
  const [dashboard, setDashboard] = useState(null);
  const [crew, setCrew] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [thresholds, setThresholds] = useState(null);
  const [effectiveness, setEffectiveness] = useState(null);
  const [pendingSurveys, setPendingSurveys] = useState(null);
  const [activeSurvey, setActiveSurvey] = useState(null); // {survey, answers, comment}
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Settings dialog
  const [showSettings, setShowSettings] = useState(false);
  const [editedThresholds, setEditedThresholds] = useState(null);

  const apiHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Fetch training effectiveness summary (GACA P1 — item 4.1.2)
  const fetchEffectiveness = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/gaca-p1/training-effectiveness/summary`, { headers: apiHeaders });
      if (res.ok) setEffectiveness(await res.json());
    } catch (err) {
      console.error('Error fetching training effectiveness:', err);
    }
  }, [token]);

  // Fetch pending effectiveness surveys for current user
  const fetchPendingSurveys = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/gaca-p1/training-effectiveness/pending`, { headers: apiHeaders });
      if (res.ok) setPendingSurveys(await res.json());
    } catch (err) {
      console.error('Error fetching pending surveys:', err);
    }
  }, [token]);

  const submitSurveyResponse = async () => {
    if (!activeSurvey) return;
    const { survey, answers, comment } = activeSurvey;
    if (Object.keys(answers).length < (pendingSurveys?.questions?.length || 0)) {
      toast.error('Please answer every question');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/gaca-p1/training-effectiveness/respond/${survey.id}`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify({ answers, comment }),
      });
      if (!res.ok) throw new Error();
      const j = await res.json();
      toast.success(`Thanks! Effectiveness score: ${j.score}%`);
      setActiveSurvey(null);
      await Promise.all([fetchPendingSurveys(), fetchEffectiveness()]);
    } catch {
      toast.error('Failed to submit survey');
    }
  };

  // Fetch dashboard
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/training-compliance/dashboard`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
        setThresholds(data.thresholds);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  }, [token]);

  // Fetch crew
  const fetchCrew = useCallback(async () => {
    try {
      const url = statusFilter !== 'all' 
        ? `${API_URL}/api/training-compliance/crew?status=${statusFilter}`
        : `${API_URL}/api/training-compliance/crew`;
      const res = await fetch(url, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setCrew(data.crew || []);
      }
    } catch (err) {
      console.error('Error fetching crew:', err);
    }
  }, [token, statusFilter]);

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/training-compliance/alerts?acknowledged=false`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
    }
  }, [token]);

  // Run alert check
  const runAlertCheck = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/training-compliance/check-alerts`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Alert check complete: ${data.results.total_issues} issues found`);
        await fetchAll();
      }
    } catch (err) {
      toast.error('Failed to run alert check');
    } finally {
      setLoading(false);
    }
  };

  // Acknowledge alert
  const acknowledgeAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/training-compliance/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        toast.success('Alert acknowledged');
        await fetchAlerts();
      }
    } catch (err) {
      toast.error('Failed to acknowledge alert');
    }
  };

  // Update thresholds
  const saveThresholds = async () => {
    if (!editedThresholds) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/training-compliance/thresholds`, {
        method: 'PUT',
        headers: apiHeaders,
        body: JSON.stringify(editedThresholds)
      });
      if (res.ok) {
        toast.success('Settings saved successfully');
        setShowSettings(false);
        await fetchDashboard();
      }
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  // Export report
  const exportReport = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/training-compliance/export`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        // Download as JSON
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `compliance_report_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Report exported successfully');
      }
    } catch (err) {
      toast.error('Failed to export report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all data
  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchDashboard(), fetchCrew(), fetchAlerts(), fetchEffectiveness(), fetchPendingSurveys()]);
    setLoading(false);
  }, [fetchDashboard, fetchCrew, fetchAlerts, fetchEffectiveness, fetchPendingSurveys]);

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    fetchCrew();
  }, [statusFilter]);

  // Filter crew by search
  const filteredCrew = crew.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      c.user_name?.toLowerCase().includes(query) ||
      c.role?.toLowerCase().includes(query) ||
      c.user_email?.toLowerCase().includes(query)
    );
  });

  // Open settings
  const openSettings = () => {
    setEditedThresholds(thresholds ? { ...thresholds } : {});
    setShowSettings(true);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="p-6 space-y-6" data-testid="training-compliance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="h-7 w-7 text-indigo-600" />
            Training Compliance
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Monitor training completion and certificate validity
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runAlertCheck} disabled={loading}>
            <Bell className="h-4 w-4 mr-2" />
            Check Alerts
          </Button>
          <Button variant="outline" size="sm" onClick={exportReport} disabled={loading}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={openSettings} data-testid="compliance-settings-btn">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button onClick={fetchAll} disabled={loading} data-testid="refresh-compliance">
            <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Total Crew</p>
                  <p className="text-2xl font-bold">{dashboard.total_crew}</p>
                </div>
                <Users className="h-8 w-8 text-indigo-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-emerald-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Compliant</p>
                  <p className="text-2xl font-bold text-emerald-600">{dashboard.compliant}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-amber-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Expiring Soon</p>
                  <p className="text-2xl font-bold text-amber-600">{dashboard.expiring_soon}</p>
                </div>
                <Clock className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-orange-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Non-Compliant</p>
                  <p className="text-2xl font-bold text-orange-600">{dashboard.non_compliant}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-red-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Overdue</p>
                  <p className="text-2xl font-bold text-red-600">{dashboard.overdue}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Overall</p>
                  <p className="text-2xl font-bold">{dashboard.overall_compliance_percentage}%</p>
                </div>
                <Gauge className="h-8 w-8 text-indigo-500" />
              </div>
              <Progress 
                value={dashboard.overall_compliance_percentage} 
                className="mt-2 h-2"
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pending Effectiveness Surveys (auto-triggered 7d post-completion) */}
      {pendingSurveys?.items?.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/40 dark:bg-amber-900/20" data-testid="te-pending-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Inbox className="h-5 w-5 text-amber-600" />
              Pending Training Effectiveness Surveys
              <Badge variant="outline" className="ml-2 text-xs">{pendingSurveys.items.length} open</Badge>
            </CardTitle>
            <CardDescription>
              You completed training {pendingSurveys.trigger_days}+ days ago. Take 60 seconds to share how effective it was — your responses feed the GACA 4.1.2 effectiveness widget.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingSurveys.items.map(s => (
                <div key={s.id} className="flex items-center justify-between p-3 rounded border bg-background" data-testid={`te-pending-${s.id}`}>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{s.module_code || s.module_id || 'Training module'}</div>
                    <div className="text-xs text-muted-foreground">Completed: {(s.completed_at_training || '').slice(0, 10)}</div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setActiveSurvey({ survey: s, answers: {}, comment: '' })}
                    data-testid={`te-pending-take-${s.id}`}
                  >
                    Take survey
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active survey dialog */}
      <Dialog open={!!activeSurvey} onOpenChange={(o) => !o && setActiveSurvey(null)}>
        <DialogContent className="max-w-2xl" data-testid="te-survey-dialog">
          <DialogHeader>
            <DialogTitle>Training Effectiveness Survey</DialogTitle>
            <DialogDescription>5-question Likert scale (1 = Strongly disagree → 5 = Strongly agree). Takes about 60 seconds.</DialogDescription>
          </DialogHeader>
          {activeSurvey && pendingSurveys && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="text-sm text-muted-foreground">
                Module: <span className="font-mono">{activeSurvey.survey.module_code || activeSurvey.survey.module_id}</span>
              </div>
              {pendingSurveys.questions.map(q => (
                <div key={q.id} className="space-y-2" data-testid={`te-q-${q.id}`}>
                  <div className="text-sm font-medium">{q.text}</div>
                  <div className="flex gap-2 flex-wrap">
                    {[1, 2, 3, 4, 5].map(v => {
                      const checked = activeSurvey.answers[q.id] === v;
                      const labels = ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'];
                      return (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setActiveSurvey({ ...activeSurvey, answers: { ...activeSurvey.answers, [q.id]: v } })}
                          className={cn(
                            'px-3 py-2 rounded-md border text-xs flex flex-col items-center min-w-[78px] transition-colors',
                            checked
                              ? 'bg-emerald-500/15 border-emerald-500/60 text-emerald-700 dark:text-emerald-300 font-medium'
                              : 'bg-background hover:border-foreground/30'
                          )}
                          data-testid={`te-q-${q.id}-${v}`}
                        >
                          <span className="font-bold">{v}</span>
                          <span className="text-[10px] mt-0.5 opacity-70 leading-tight text-center">{labels[v - 1]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div className="space-y-1.5">
                <Label className="text-xs">Optional comment</Label>
                <Input
                  value={activeSurvey.comment}
                  onChange={(e) => setActiveSurvey({ ...activeSurvey, comment: e.target.value })}
                  placeholder="What worked? What could be improved?"
                  data-testid="te-survey-comment"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActiveSurvey(null)}>Cancel</Button>
            <Button onClick={submitSurveyResponse} data-testid="te-survey-submit">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Training Effectiveness summary — GACA item 4.1.2 */}
      {effectiveness && (
        <Card data-testid="training-effectiveness-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Gauge className="h-5 w-5 text-violet-500" />
              Training Effectiveness
              <Badge variant="outline" className="ml-2 text-xs">GACA 4.1.2</Badge>
            </CardTitle>
            <CardDescription>Average quiz performance and post-training survey response — measures whether training is achieving its objectives.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Avg quiz score</div>
                <div className="text-2xl font-bold tabular-nums" data-testid="te-avg-quiz">{effectiveness.overall.avg_quiz_score}%</div>
                <Progress value={effectiveness.overall.avg_quiz_score} className="h-1.5 mt-2" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Completed trainings</div>
                <div className="text-2xl font-bold tabular-nums" data-testid="te-completed">{effectiveness.overall.completed_trainings}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Avg survey score</div>
                <div className="text-2xl font-bold tabular-nums" data-testid="te-avg-survey">{effectiveness.overall.avg_survey_score}%</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Survey responses</div>
                <div className="text-2xl font-bold tabular-nums" data-testid="te-survey-count">{effectiveness.overall.survey_responses}</div>
              </div>
            </div>
            {effectiveness.per_module?.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Per module</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {effectiveness.per_module.slice(0, 6).map((m, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded border text-sm" data-testid={`te-module-${m.module}`}>
                      <span className="font-mono text-xs truncate">{m.module}</span>
                      <span className="tabular-nums font-medium">{m.avg_quiz_score}% <span className="text-muted-foreground text-xs">({m.completed})</span></span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="crew" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Crew Status
          </TabsTrigger>
          <TabsTrigger value="alerts" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Alerts
            {alerts.length > 0 && (
              <Badge className="ml-1 bg-red-500">{alerts.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Compliance by Role */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-indigo-500" />
                  Compliance by Role
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.by_role?.length > 0 ? (
                  <div className="space-y-4">
                    {dashboard.by_role.map((role, idx) => (
                      <div key={idx}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium capitalize">{role.role.replace('_', ' ')}</span>
                          <span className="text-sm text-gray-500">
                            {role.compliant}/{role.total} ({role.compliance_percentage}%)
                          </span>
                        </div>
                        <Progress 
                          value={role.compliance_percentage} 
                          className={cn(
                            "h-3",
                            role.compliance_percentage >= 80 ? "bg-emerald-100" :
                            role.compliance_percentage >= 50 ? "bg-amber-100" : "bg-red-100"
                          )}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No role data available</p>
                )}
              </CardContent>
            </Card>

            {/* Status Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-indigo-500" />
                  Status Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.status_breakdown && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        <span className="font-medium">Compliant</span>
                      </div>
                      <Badge className="bg-emerald-500">{dashboard.status_breakdown.compliant_percentage}%</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-amber-600" />
                        <span className="font-medium">Expiring Soon</span>
                      </div>
                      <Badge className="bg-amber-500">{dashboard.status_breakdown.expiring_percentage}%</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-orange-600" />
                        <span className="font-medium">Non-Compliant</span>
                      </div>
                      <Badge className="bg-orange-500">{dashboard.status_breakdown.non_compliant_percentage}%</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-red-600" />
                        <span className="font-medium">Overdue</span>
                      </div>
                      <Badge className="bg-red-500">{dashboard.status_breakdown.overdue_percentage}%</Badge>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Email Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="h-5 w-5 text-indigo-500" />
                  Notification Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Mail className="h-5 w-5" />
                      <span>Email Notifications</span>
                    </div>
                    <Badge className={dashboard?.email_enabled ? "bg-emerald-500" : "bg-gray-400"}>
                      {dashboard?.email_enabled ? "Enabled" : "Not Configured"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Inbox className="h-5 w-5" />
                      <span>In-App Notifications</span>
                    </div>
                    <Badge className="bg-emerald-500">Enabled</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Bell className="h-5 w-5" />
                      <span>Pending Alerts</span>
                    </div>
                    <Badge className={dashboard?.pending_alerts > 0 ? "bg-red-500" : "bg-gray-400"}>
                      {dashboard?.pending_alerts || 0}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Thresholds Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="h-5 w-5 text-indigo-500" />
                  Current Thresholds
                </CardTitle>
                <CardDescription>Industry-standard compliance settings</CardDescription>
              </CardHeader>
              <CardContent>
                {thresholds && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Min Completion %</span>
                      <span className="font-medium">{thresholds.min_completion_percentage}%</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Flag Below</span>
                      <span className="font-medium">{thresholds.flag_below_completion}%</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Critical Threshold</span>
                      <span className="font-medium">{thresholds.critical_completion}%</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Recurrent Training</span>
                      <span className="font-medium">{thresholds.recurrent_training_months} months</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Max Overdue Days</span>
                      <span className="font-medium">{thresholds.max_overdue_days} days</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Crew Status Tab */}
        <TabsContent value="crew" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, role, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={statusFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('all')}
              >
                All
              </Button>
              <Button
                variant={statusFilter === 'compliant' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('compliant')}
                className="border-emerald-300"
              >
                Compliant
              </Button>
              <Button
                variant={statusFilter === 'expiring_soon' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('expiring_soon')}
                className="border-amber-300"
              >
                Expiring
              </Button>
              <Button
                variant={statusFilter === 'non_compliant' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('non_compliant')}
                className="border-orange-300"
              >
                Non-Compliant
              </Button>
              <Button
                variant={statusFilter === 'overdue' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('overdue')}
                className="border-red-300"
              >
                Overdue
              </Button>
            </div>
          </div>

          {/* Crew Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50 dark:bg-gray-800">
                      <th className="text-left py-3 px-4 font-medium">Crew Member</th>
                      <th className="text-left py-3 px-4 font-medium">Role</th>
                      <th className="text-left py-3 px-4 font-medium">Completion</th>
                      <th className="text-left py-3 px-4 font-medium">Certificates</th>
                      <th className="text-left py-3 px-4 font-medium">Next Expiry</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCrew.map((member) => {
                      const config = statusConfig[member.status] || statusConfig.non_compliant;
                      const StatusIcon = config.icon;
                      
                      return (
                        <tr key={member.user_id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className={cn(
                                "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold",
                                config.color
                              )}>
                                {member.compliance_percentage.toFixed(0)}
                              </div>
                              <div>
                                <p className="font-medium">{member.user_name}</p>
                                <p className="text-xs text-gray-500">{member.user_email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline" className="capitalize">
                              {member.role?.replace('_', ' ') || 'N/A'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="w-32">
                              <div className="flex justify-between text-xs mb-1">
                                <span>{member.completed_modules}/{member.total_required_modules}</span>
                                <span>{member.compliance_percentage}%</span>
                              </div>
                              <Progress value={member.compliance_percentage} className="h-2" />
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-emerald-100 text-emerald-800">{member.certificates_valid} valid</Badge>
                              {member.certificates_expiring_soon > 0 && (
                                <Badge className="bg-amber-100 text-amber-800">{member.certificates_expiring_soon} expiring</Badge>
                              )}
                              {member.certificates_expired > 0 && (
                                <Badge className="bg-red-100 text-red-800">{member.certificates_expired} expired</Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {member.days_until_next_expiry !== null ? (
                              <span className={cn(
                                "font-medium",
                                member.days_until_next_expiry <= 7 ? "text-red-600" :
                                member.days_until_next_expiry <= 30 ? "text-amber-600" : "text-gray-600"
                              )}>
                                {member.days_until_next_expiry} days
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={cn(config.bgLight, config.text, "flex items-center gap-1 w-fit")}>
                              <StatusIcon className="h-3 w-3" />
                              {config.label}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredCrew.length === 0 && (
                      <tr>
                        <td colSpan="6" className="py-8 text-center text-gray-500">
                          No crew members found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-indigo-500" />
                  Pending Alerts
                </span>
                <Badge className="bg-red-500">{alerts.length}</Badge>
              </CardTitle>
              <CardDescription>Compliance issues requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length > 0 ? (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {alerts.map((alert) => (
                      <div
                        key={alert.id}
                        className={cn(
                          "p-4 rounded-lg border-l-4",
                          alert.severity === 'critical' ? "border-red-500 bg-red-50 dark:bg-red-900/20" :
                          alert.severity === 'high' ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20" :
                          alert.severity === 'medium' ? "border-amber-500 bg-amber-50 dark:bg-amber-900/20" :
                          "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        )}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={severityColors[alert.severity]}>
                                {alert.severity.toUpperCase()}
                              </Badge>
                              <span className="text-sm text-gray-500">
                                {formatDate(alert.created_at)}
                              </span>
                            </div>
                            <p className="font-medium">{alert.user_name}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{alert.message}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              {alert.sent_email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  Email sent
                                </span>
                              )}
                              {alert.sent_inapp && (
                                <span className="flex items-center gap-1">
                                  <Inbox className="h-3 w-3" />
                                  In-app sent
                                </span>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => acknowledgeAlert(alert.id)}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Acknowledge
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="py-12 text-center">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500 mb-4" />
                  <p className="text-gray-500">No pending alerts</p>
                  <p className="text-sm text-gray-400 mt-1">All compliance issues have been addressed</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Compliance Settings
            </DialogTitle>
          </DialogHeader>
          
          {editedThresholds && (
            <div className="space-y-6 py-4">
              {/* Completion Thresholds */}
              <div>
                <h4 className="font-medium mb-3">Completion Thresholds</h4>
                <div className="space-y-4">
                  <div>
                    <Label>Minimum Compliance % (Industry: 80%)</Label>
                    <div className="flex items-center gap-4 mt-2">
                      <Slider
                        value={[editedThresholds.min_completion_percentage || 80]}
                        onValueChange={([val]) => setEditedThresholds({...editedThresholds, min_completion_percentage: val})}
                        max={100}
                        step={5}
                        className="flex-1"
                      />
                      <span className="w-12 text-right font-medium">
                        {editedThresholds.min_completion_percentage || 80}%
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label>Flag Below % (Industry: 80%)</Label>
                    <div className="flex items-center gap-4 mt-2">
                      <Slider
                        value={[editedThresholds.flag_below_completion || 80]}
                        onValueChange={([val]) => setEditedThresholds({...editedThresholds, flag_below_completion: val})}
                        max={100}
                        step={5}
                        className="flex-1"
                      />
                      <span className="w-12 text-right font-medium">
                        {editedThresholds.flag_below_completion || 80}%
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label>Critical Threshold % (Industry: 50%)</Label>
                    <div className="flex items-center gap-4 mt-2">
                      <Slider
                        value={[editedThresholds.critical_completion || 50]}
                        onValueChange={([val]) => setEditedThresholds({...editedThresholds, critical_completion: val})}
                        max={100}
                        step={5}
                        className="flex-1"
                      />
                      <span className="w-12 text-right font-medium">
                        {editedThresholds.critical_completion || 50}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Expiry Warnings */}
              <div>
                <h4 className="font-medium mb-3">Expiry Warning Periods</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center justify-between p-2 border rounded">
                    <span className="text-sm">30 days</span>
                    <Switch
                      checked={editedThresholds.warning_30_days}
                      onCheckedChange={(val) => setEditedThresholds({...editedThresholds, warning_30_days: val})}
                    />
                  </div>
                  <div className="flex items-center justify-between p-2 border rounded">
                    <span className="text-sm">14 days</span>
                    <Switch
                      checked={editedThresholds.warning_14_days}
                      onCheckedChange={(val) => setEditedThresholds({...editedThresholds, warning_14_days: val})}
                    />
                  </div>
                  <div className="flex items-center justify-between p-2 border rounded">
                    <span className="text-sm">7 days</span>
                    <Switch
                      checked={editedThresholds.warning_7_days}
                      onCheckedChange={(val) => setEditedThresholds({...editedThresholds, warning_7_days: val})}
                    />
                  </div>
                  <div className="flex items-center justify-between p-2 border rounded">
                    <span className="text-sm">1 day</span>
                    <Switch
                      checked={editedThresholds.warning_1_day}
                      onCheckedChange={(val) => setEditedThresholds({...editedThresholds, warning_1_day: val})}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Notifications */}
              <div>
                <h4 className="font-medium mb-3">Notification Channels</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-2 border rounded">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      <span className="text-sm">Email Notifications</span>
                    </div>
                    <Switch
                      checked={editedThresholds.enable_email_notifications}
                      onCheckedChange={(val) => setEditedThresholds({...editedThresholds, enable_email_notifications: val})}
                    />
                  </div>
                  <div className="flex items-center justify-between p-2 border rounded">
                    <div className="flex items-center gap-2">
                      <Inbox className="h-4 w-4" />
                      <span className="text-sm">In-App Notifications</span>
                    </div>
                    <Switch
                      checked={editedThresholds.enable_inapp_notifications}
                      onCheckedChange={(val) => setEditedThresholds({...editedThresholds, enable_inapp_notifications: val})}
                    />
                  </div>
                  <div className="flex items-center justify-between p-2 border rounded">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span className="text-sm">Notify Supervisors</span>
                    </div>
                    <Switch
                      checked={editedThresholds.notify_supervisors}
                      onCheckedChange={(val) => setEditedThresholds({...editedThresholds, notify_supervisors: val})}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Cancel
            </Button>
            <Button onClick={saveThresholds} disabled={loading}>
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
